import React, { useState, useEffect } from 'react';
import socketService from '@/services/Common/SocketService';
import chatService from '@/services/Common/ChatService';
import ticketService from '@/services/Common/TicketService';
import httpClient from '@/services/httpClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { UserRole } from '@/constants/roles';
import './AdminSupportDashboard.css';

interface PendingChat {
  conversationId: string;
  user: { uuid: string; email: string };
  startedAt: Date;
  messageCount: number;
}

interface ActiveChat {
  conversationId: string;
  user: { uuid: string; email: string };
  lastMessageAt: Date;
  unreadCount: number;
}

interface AgentStatus {
  status: 'available' | 'busy' | 'away' | 'offline';
  currentActiveChats: number;
  maxConcurrentChats: number;
}

const AdminSupportDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'chats' | 'tickets'>('chats');
  const [agentStatus, setAgentStatus] = useState<AgentStatus>({
    status: 'offline',
    currentActiveChats: 0,
    maxConcurrentChats: 5,
  });
  const [pendingChats, setPendingChats] = useState<PendingChat[]>([]);
  const [activeChats, setActiveChats] = useState<ActiveChat[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    initializeAgent();

    return () => {
      if (socketService.isConnected()) {
        changeAgentStatus('offline');
      }
    };
  }, []);

  const initializeAgent = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      await socketService.connect(token);
      setIsConnected(true);

      // Set initial status to available
      await changeAgentStatus('available');

      // Load pending and active chats
      await loadPendingChats();
      await loadActiveChats();

      // Listen for new chat requests
      socketService.on('chat:new_request', handleNewChatRequest);
      socketService.on('chat:request_accepted', handleChatAccepted);
    } catch (error) {
      console.error('Error initializing agent:', error);
    }
  };

  const handleNewChatRequest = (data: any) => {
    setPendingChats((prev) => [
      {
        conversationId: data.conversationId,
        user: data.user,
        startedAt: new Date(data.timestamp),
        messageCount: 0,
      },
      ...prev,
    ]);
  };

  const handleChatAccepted = (data: any) => {
    setPendingChats((prev) => prev.filter((c) => c.conversationId !== data.conversationId));
  };

  const loadPendingChats = async () => {
    try {
      const response = await new Promise<any>((resolve) => {
        socketService.emit('agent:get_pending_chats', (res: any) => resolve(res));
      });

      if (response.success) {
        setPendingChats(response.conversations || []);
      }
    } catch (error) {
      console.error('Error loading pending chats:', error);
    }
  };

  const loadActiveChats = async () => {
    try {
      const response = await new Promise<any>((resolve) => {
        socketService.emit('agent:get_active_chats', (res: any) => resolve(res));
      });

      if (response.success) {
        setActiveChats(response.conversations || []);
      }
    } catch (error) {
      console.error('Error loading active chats:', error);
    }
  };

  const changeAgentStatus = async (newStatus: AgentStatus['status']) => {
    try {
      const response = await new Promise<any>((resolve) => {
        socketService.emit('agent:change_status', { status: newStatus }, (res: any) =>
          resolve(res)
        );
      });

      if (response.success) {
        setAgentStatus((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error changing status:', error);
    }
  };

  const acceptChat = async (conversationId: string) => {
    try {
      const response = await new Promise<any>((resolve) => {
        socketService.emit('agent:accept_chat', { conversationId }, (res: any) => resolve(res));
      });

      if (response.success) {
        await loadPendingChats();
        await loadActiveChats();
      }
    } catch (error: any) {
      alert(error.message || 'Failed to accept chat');
    }
  };

  return (
    <div className="admin-support-dashboard">
      <div className="dashboard-header">
        <h1>Support Dashboard</h1>
        <div className="agent-status-controls">
          <label>Status:</label>
          <select
            value={agentStatus.status}
            onChange={(e) =>
              changeAgentStatus(e.target.value as AgentStatus['status'])
            }
            disabled={!isConnected}
          >
            <option value="available">Available</option>
            <option value="busy">Busy</option>
            <option value="away">Away</option>
            <option value="offline">Offline</option>
          </select>
          <span className={`status-indicator status-${agentStatus.status}`}>
            {isConnected ? '●' : '○'}
          </span>
        </div>
      </div>

      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'chats' ? 'active' : ''}`}
          onClick={() => setActiveTab('chats')}
        >
          Live Chats
          {pendingChats.length > 0 && (
            <span className="badge">{pendingChats.length}</span>
          )}
        </button>
        <button
          className={`tab-button ${activeTab === 'tickets' ? 'active' : ''}`}
          onClick={() => setActiveTab('tickets')}
        >
          Support Tickets
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'chats' ? (
          <div className="chats-section">
            <div className="chat-column">
              <h2>Pending Chats ({pendingChats.length})</h2>
              {pendingChats.length === 0 ? (
                <p className="empty-state">No pending chats</p>
              ) : (
                <div className="chat-list">
                  {pendingChats.map((chat) => (
                    <div key={chat.conversationId} className="chat-card">
                      <div className="chat-info">
                        <strong>{chat.user.email}</strong>
                        <small>
                          {new Date(chat.startedAt).toLocaleTimeString()}
                        </small>
                      </div>
                      <button
                        className="accept-button"
                        onClick={() => acceptChat(chat.conversationId)}
                        disabled={agentStatus.status !== 'available'}
                      >
                        Accept
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="chat-column">
              <h2>
                Active Chats ({agentStatus.currentActiveChats}/
                {agentStatus.maxConcurrentChats})
              </h2>
              {activeChats.length === 0 ? (
                <p className="empty-state">No active chats</p>
              ) : (
                <div className="chat-list">
                  {activeChats.map((chat) => (
                    <div key={chat.conversationId} className="chat-card">
                      <div className="chat-info">
                        <strong>{chat.user.email}</strong>
                        {chat.unreadCount > 0 && (
                          <span className="unread-badge">{chat.unreadCount}</span>
                        )}
                        <small>
                          Last message:{' '}
                          {new Date(chat.lastMessageAt).toLocaleTimeString()}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <TicketManagement />
        )}
      </div>
    </div>
  );
};

// Ticket Management Component with Detail View
const ROLE_LABELS: Record<number, string> = { 2: 'Trainer', 3: 'Admin', 4: 'Super Admin' };

const TicketManagement: React.FC = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', priority: '' });
  const [expandedTicketId, setExpandedTicketId] = useState<number | null>(null);
  const [ticketDetails, setTicketDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [staffList, setStaffList] = useState<any[]>([]);

  useEffect(() => {
    loadTickets();
  }, [filter]);

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      const response = await httpClient.get<any>('/support/admin/staff');
      setStaffList(response.staff || []);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.priority) params.append('priority', filter.priority);

      const response = await httpClient.get<any>(
        `/support/admin/tickets?${params.toString()}`
      );
      setTickets(response.tickets || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTicketDetails = async (ticketId: number) => {
    if (expandedTicketId === ticketId) {
      setExpandedTicketId(null);
      setTicketDetails(null);
      setReplyText('');
      return;
    }

    setExpandedTicketId(ticketId);
    setLoadingDetails(true);
    setReplyText('');
    try {
      const details = await ticketService.getTicketDetails(ticketId);
      setTicketDetails(details);
    } catch (error) {
      console.error('Error loading ticket details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !expandedTicketId) return;
    setSendingReply(true);
    try {
      await ticketService.addTicketReply(expandedTicketId, replyText.trim());
      setReplyText('');
      // Refresh details
      const details = await ticketService.getTicketDetails(expandedTicketId);
      setTicketDetails(details);
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSendingReply(false);
    }
  };

  const assignTicket = async (ticketId: number, agentId: string) => {
    if (!agentId) return;
    try {
      await httpClient.patch(`/support/admin/tickets/${ticketId}/assign`, {
        assigned_to: agentId,
      });
      await loadTickets();
    } catch (error: any) {
      alert('Failed to assign ticket');
    }
  };

  const updateStatus = async (ticketId: number, status: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await httpClient.patch(`/support/admin/tickets/${ticketId}/status`, { status });
      await loadTickets();
      // Refresh details if this ticket is expanded
      if (expandedTicketId === ticketId) {
        const details = await ticketService.getTicketDetails(ticketId);
        setTicketDetails(details);
      }
    } catch (error: any) {
      alert('Failed to update status');
    }
  };

  if (loading) return <div>Loading tickets...</div>;

  return (
    <div className="ticket-management">
      <div className="ticket-filters">
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="assigned">Assigned</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select value={filter.priority} onChange={(e) => setFilter({ ...filter, priority: e.target.value })}>
          <option value="">All Priority</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      <div className="ticket-list">
        {tickets.map((ticket) => (
          <div key={ticket.id} className={`ticket-card ${expandedTicketId === ticket.id ? 'ticket-card-expanded' : ''}`}>
            <div className="ticket-card-clickable" onClick={() => toggleTicketDetails(ticket.id)}>
              <div className="ticket-header">
                <strong>{ticket.ticket_number}</strong>
                <span className={`status-badge status-${ticket.status}`}>
                  {ticket.status.replace('_', ' ')}
                </span>
                <span className={`priority-badge priority-${ticket.priority}`}>
                  {ticket.priority}
                </span>
              </div>
              <h3>{ticket.subject}</h3>
              <p>User: {ticket.user_email}</p>
              <p>Created: {new Date(ticket.created_at).toLocaleDateString()}</p>
            </div>

            <div className="ticket-actions">
              {isAdmin && (
                <select
                  value={ticket.assigned_to || ''}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => assignTicket(ticket.id, e.target.value)}
                  className="ticket-assign-select"
                >
                  <option value="">Assign to...</option>
                  {staffList.map((s: any) => (
                    <option key={s.uuid} value={s.uuid}>
                      {s.email} ({ROLE_LABELS[s.role_id] || 'Staff'})
                    </option>
                  ))}
                </select>
              )}
              <select
                value={ticket.status}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => updateStatus(ticket.id, e.target.value)}
                className="ticket-status-select"
              >
                <option value="open">Open</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="pending_user">Pending User</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Expanded Detail View */}
            {expandedTicketId === ticket.id && (
              <div className="ticket-detail-panel">
                {loadingDetails ? (
                  <div className="ticket-detail-loading">Loading details...</div>
                ) : ticketDetails ? (
                  <>
                    {/* Description */}
                    <div className="ticket-description">
                      <h4>Description</h4>
                      <div className="ticket-description-text">{ticketDetails.description || 'No description provided.'}</div>
                    </div>

                    {ticketDetails.assigned_agent_email && (
                      <p className="ticket-assigned-info">Assigned to: {ticketDetails.assigned_agent_email}</p>
                    )}

                    {ticketDetails.resolution_notes && (
                      <div className="ticket-resolution">
                        <h4>Resolution Notes</h4>
                        <p>{ticketDetails.resolution_notes}</p>
                      </div>
                    )}

                    {/* Replies */}
                    <div className="ticket-replies-section">
                      <h4>Replies ({ticketDetails.replies?.length || 0})</h4>
                      {(!ticketDetails.replies || ticketDetails.replies.length === 0) ? (
                        <p className="ticket-no-replies">No replies yet.</p>
                      ) : (
                        <div className="ticket-replies-list">
                          {ticketDetails.replies.map((reply: any) => (
                            <div key={reply.id} className={`ticket-reply ${reply.user_type === 'agent' ? 'ticket-reply-agent' : 'ticket-reply-user'} ${reply.is_internal ? 'ticket-reply-internal' : ''}`}>
                              <div className="ticket-reply-header">
                                <span className="ticket-reply-sender">
                                  {reply.user_type === 'agent' ? 'Agent' : 'User'}{reply.is_internal ? ' (Internal)' : ''}
                                </span>
                                <span className="ticket-reply-email">{reply.user_email}</span>
                                <span className="ticket-reply-date">{new Date(reply.created_at).toLocaleString()}</span>
                              </div>
                              <div className="ticket-reply-message">{reply.message}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Reply Input */}
                    {ticket.status !== 'closed' && (
                      <div className="ticket-reply-form">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply..."
                          rows={3}
                          className="ticket-reply-textarea"
                        />
                        <button
                          onClick={handleSendReply}
                          disabled={!replyText.trim() || sendingReply}
                          className="ticket-reply-send-btn"
                        >
                          {sendingReply ? 'Sending...' : 'Send Reply'}
                        </button>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminSupportDashboard;
